import {
    EHeroPrime,
    HERO_FPS,
    IHeroPart,
    heroFrameAt,
    heroLoadPercent,
    heroPartUrl,
    heroPartsToPrime,
    heroPrimeComplete,
    heroPrimeOutcome,
} from './home-hero.logic';

/**
 * Parts downloaded during this visit, by address. A move away from the home page stops the player and
 * frees its videos, but the bytes stay here, so coming back does not download the video again. Lives
 * only in the browser: the player is never created on the server
 */
const DOWNLOADED_PARTS: Map<string, Blob> = new Map<string, Blob>();

/** A frame longer than this is counted as this, so a stalled tab does not jump the scrub */
const MAX_FRAME_SECONDS: number = 0.05;

/**
 * Calls `frame` on every animation frame while `host` is on screen, and `hidden` when it leaves it:
 * a page scrolled past the hero runs no loop and measures nothing. Returns the stop
 */
export function heroFrameLoop(
    win: Window & typeof globalThis,
    host: HTMLElement,
    frame: (seconds: number) => void,
    hidden: () => void
): () => void {
    let frameId: number = 0;
    let running: boolean = false;
    let last: number = 0;
    const tick: (now: number) => void = (now: number): void => {
        frameId = win.requestAnimationFrame(tick);
        frame(Math.min(MAX_FRAME_SECONDS, (now - last) / 1000));
        last = now;
    };
    const run: (on: boolean) => void = (on: boolean): void => {
        if (on === running) {
            return;
        }
        running = on;
        if (on) {
            last = win.performance.now();
            frameId = win.requestAnimationFrame(tick);
        } else {
            win.cancelAnimationFrame(frameId);
            hidden();
        }
    };
    const observer: IntersectionObserver = new win.IntersectionObserver((entries: IntersectionObserverEntry[]): void => {
        run(entries.some((entry: IntersectionObserverEntry): boolean => entry.isIntersecting));
    });
    observer.observe(host);
    run(true);
    return (): void => {
        observer.disconnect();
        run(false);
    };
}

/**
 * Gestures inside which a browser lets a video start, so every opened part is started and paused
 * there once (Safari on an iPhone in Low Power Mode refuses it otherwise). An iPhone grants the start
 * on the end of a touch, so the ends are listened to together with the beginnings
 */
const PRIME_EVENTS: readonly string[] = ['touchstart', 'touchend', 'pointerdown', 'pointerup', 'click', 'keydown'];

/**
 * Hands every gesture of the person to the player until each part has played once. Parts open one
 * after another, so one gesture is not enough; the listeners go when all parts are settled or the
 * page is left (`pageLeave`)
 */
export function heroPrimeOnGestures(win: Window & typeof globalThis, player: () => HeroPlayer | null, pageLeave: AbortSignal): void {
    const primeAbort: AbortController = new win.AbortController();
    pageLeave.addEventListener('abort', (): void => {
        primeAbort.abort();
    });
    const prime: () => void = (): void => {
        if (player()?.primeOnGesture()) {
            primeAbort.abort();
        }
    };
    for (const type of PRIME_EVENTS) {
        win.addEventListener(type, prime, { capture: true, passive: true, signal: primeAbort.signal });
    }
}

/** Every part of the variant is already downloaded during this visit: coming back needs no hold */
export function heroPartsDownloaded(source: IHeroPlayerSource, parts: readonly IHeroPart[]): boolean {
    return parts.every((part: IHeroPart): boolean => DOWNLOADED_PARTS.has(heroPartUrl(source.base, part.name, source.size)));
}

/** What the player tells the component */
export interface IHeroPlayerEvents {
    /** More bytes arrived or a part became ready: the hint redraws */
    readonly loading: () => void;
    /** A part was seeked and is the one to show: the component makes it the visible video */
    readonly shown: (part: number) => void;
    /** A part could not be downloaded or opened: the rest is not tried */
    readonly failed: () => void;
}

/** Where the parts are and at which width */
export interface IHeroPlayerSource {
    readonly base: string;
    readonly size: number;
}

/** Half a frame: a seek closer than this lands on the same frame */
const HALF_FRAME: number = 0.5 / HERO_FPS;

/**
 * Download of the hero parts and seeking of their videos, ported from the owner's reference page.
 * Parts come one after another, each fully into a Blob; the first one ready starts the scrub. Every
 * video holds at most one seek: a new one starts on `seeked` of the previous. The frame is decided by
 * the pure `heroFrameAt`; here are only requests, videos and their events. The window comes from the
 * component, which takes it by injection
 */
export class HeroPlayer {
    readonly #window: Window & typeof globalThis;
    readonly #videos: readonly HTMLVideoElement[];
    readonly #parts: IHeroPart[];
    readonly #source: IHeroPlayerSource;
    readonly #events: IHeroPlayerEvents;
    readonly #abort: AbortController;
    readonly #ready: boolean[];
    readonly #received: number[];
    readonly #sizes: number[];
    /** Time each video should stand on; `-1` — none asked yet */
    readonly #wanted: number[];
    /** Time each video stands on after its last seek; `-1` — never seeked */
    readonly #landed: number[];
    readonly #seeking: boolean[];
    /** The part's video has its Blob address; its frames may still be unknown */
    readonly #sourced: boolean[];
    /** The part was started and paused once, or cannot play at all: nothing more to try */
    readonly #primeSettled: boolean[];
    /** A start of the part is in flight */
    readonly #priming: boolean[];
    readonly #urls: string[] = [];
    /** When the part now opening got its Blob address; `-1` — no part is waiting for its frames */
    #openWaitSince: number = -1;
    #started: boolean = false;
    #stopped: boolean = false;
    #targetPart: number = 0;
    #shownPart: number = -1;

    constructor(
        win: Window & typeof globalThis,
        videos: readonly HTMLVideoElement[],
        parts: readonly IHeroPart[],
        source: IHeroPlayerSource,
        events: IHeroPlayerEvents
    ) {
        this.#window = win;
        this.#abort = new win.AbortController();
        this.#videos = videos;
        this.#parts = [...parts];
        this.#source = source;
        this.#events = events;
        this.#ready = parts.map((): boolean => false);
        this.#received = parts.map((): number => 0);
        this.#sizes = parts.map((): number => 0);
        this.#wanted = parts.map((): number => -1);
        this.#landed = parts.map((): number => -1);
        this.#seeking = parts.map((): boolean => false);
        this.#sourced = parts.map((): boolean => false);
        this.#primeSettled = parts.map((): boolean => false);
        this.#priming = parts.map((): boolean => false);
        videos.forEach((video: HTMLVideoElement, part: number): void => {
            // The markup's `muted` is an attribute set after the element was created, which leaves the
            // property false; a browser lets an unmuted video start only on a gesture
            video.muted = true;
            video.defaultMuted = true;
            video.playsInline = true;
            video.addEventListener(
                'seeked',
                (): void => {
                    this.#seeked(part);
                },
                { signal: this.#abort.signal }
            );
        });
    }

    /** Ready parts in order */
    public ready(): readonly boolean[] {
        return this.#ready;
    }

    /** Share of the variant's bytes received, for the hint */
    public percent(): number {
        return heroLoadPercent(this.#received, this.#sizes);
    }

    /** Downloads the parts one after another; a second call does nothing */
    public start(): void {
        if (this.#started || this.#stopped) {
            return;
        }
        this.#started = true;
        void this.#loadAll();
    }

    /** Shows the frame of a progress; a part not downloaded yet holds the last frame of the one before */
    public scrub(progress: number): void {
        this.#holdStill();
        const target: { part: number; time: number } | null = heroFrameAt(progress, this.#parts, this.#ready);
        if (!target) {
            return;
        }
        const video: HTMLVideoElement = this.#videos[target.part];
        const time: number = Math.min(target.time, (video.duration || Number.POSITIVE_INFINITY) - 0.01);
        this.#targetPart = target.part;
        this.#seek(target.part, time);
        const landed: boolean = !this.#seeking[target.part] && Math.abs(this.#landed[target.part] - time) <= HALF_FRAME;
        if (this.#shownPart === target.part || landed) {
            this.#show(target.part);
        }
    }

    /** Since when a downloaded part waits for its video to learn its frames; `-1` — none waits */
    public openWaitSince(): number {
        return this.#openWaitSince;
    }

    /**
     * Called inside a gesture of the person, where a browser always lets a video start: every opened
     * part that has not played yet is started and paused. `true` — every part is settled, the
     * gesture listeners can go
     */
    public primeOnGesture(): boolean {
        if (this.#stopped) {
            return true;
        }
        heroPartsToPrime(this.#sourced, this.#primeSettled, this.#priming).forEach((part: number): void => {
            this.#prime(part);
        });
        return heroPrimeComplete(this.#primeSettled);
    }

    /**
     * The hero left the page: requests are cut, videos let go of their frames, Blob addresses are
     * released. Without this every visit to the home page would add memory to the last one
     */
    public stop(): void {
        this.#stopped = true;
        this.#abort.abort();
        this.#videos.forEach((video: HTMLVideoElement): void => {
            video.removeAttribute('src');
            video.load();
        });
        this.#urls.forEach((url: string): void => {
            this.#window.URL.revokeObjectURL(url);
        });
    }

    async #loadAll(): Promise<void> {
        for (let part: number = 0; part < this.#parts.length; part++) {
            try {
                const blob: Blob = await this.#download(part);
                this.#openWaitSince = this.#window.performance.now();
                await this.#open(part, blob);
                this.#openWaitSince = -1;
            } catch {
                // The rest is not tried: the scrub stays on what is ready, or on the poster
                if (!this.#stopped) {
                    this.#events.failed();
                }
                return;
            }
            this.#ready[part] = true;
            this.#events.loading();
        }
    }

    /** A part comes in chunks: the received bytes feed the percent of the hint */
    async #download(part: number): Promise<Blob> {
        const url: string = heroPartUrl(this.#source.base, this.#parts[part].name, this.#source.size);
        // Back on the home page within the same visit: the part is already here, no network at all
        const kept: Blob | undefined = DOWNLOADED_PARTS.get(url);
        if (kept) {
            this.#sizes[part] = kept.size;
            this.#received[part] = kept.size;
            return kept;
        }
        const blob: Blob = await this.#fetchPart(part, url);
        DOWNLOADED_PARTS.set(url, blob);
        return blob;
    }

    async #fetchPart(part: number, url: string): Promise<Blob> {
        const response: Response = await this.#window.fetch(url, { signal: this.#abort.signal });
        if (!response.ok || !response.body) {
            throw new Error(`HTTP ${String(response.status)}`);
        }
        this.#sizes[part] = Number(response.headers.get('content-length')) || 0;
        const chunks: Uint8Array<ArrayBuffer>[] = [];
        const reader: ReadableStreamDefaultReader<Uint8Array<ArrayBuffer>> = response.body.getReader();
        for (;;) {
            const chunk: ReadableStreamReadResult<Uint8Array<ArrayBuffer>> = await reader.read();
            if (chunk.done) {
                break;
            }
            chunks.push(chunk.value);
            this.#received[part] += chunk.value.length;
            this.#events.loading();
        }
        return new this.#window.Blob(chunks, { type: 'video/mp4' });
    }

    /** The downloaded part goes into its video; it is ready when the video knows its frames */
    #open(part: number, blob: Blob): Promise<void> {
        if (this.#stopped) {
            return Promise.reject(new Error('stopped'));
        }
        const video: HTMLVideoElement = this.#videos[part];
        const url: string = this.#window.URL.createObjectURL(blob);
        this.#urls.push(url);
        return new Promise<void>((resolve: () => void, reject: (reason: Error) => void): void => {
            const options: AddEventListenerOptions = { once: true, signal: this.#abort.signal };
            video.addEventListener(
                'loadeddata',
                (): void => {
                    resolve();
                },
                options
            );
            video.addEventListener(
                'loadedmetadata',
                (): void => {
                    resolve();
                },
                options
            );
            video.addEventListener(
                'error',
                (): void => {
                    reject(new Error('decode'));
                },
                options
            );
            // The markup has `preload="none"` so the browser fetches nothing by itself; with it a
            // Blob address is not opened, and `loadeddata` never comes
            video.preload = 'auto';
            video.src = url;
            video.load();
            // Safari on an iPhone (Low Power Mode above all) reads no media of a video that never
            // played: no `loadedmetadata` until a start. The start is tried at once, and again on a gesture
            this.#sourced[part] = true;
            this.#prime(part);
        });
    }

    /**
     * Starts the part and pauses it in the same turn the start is granted, before a frame is painted:
     * the video never visibly runs. A rejected start (no gesture yet) waits for `primeOnGesture`
     */
    #prime(part: number): void {
        const video: HTMLVideoElement = this.#videos[part];
        this.#priming[part] = true;
        // Paused on `playing` itself: a start that runs although its promise is rejected (WebKit does
        // so when the media arrives after the call) would otherwise play the part to its end
        video.addEventListener(
            'playing',
            (): void => {
                video.pause();
            },
            { once: true, signal: this.#abort.signal }
        );
        void video.play().then(
            (): void => {
                video.pause();
                this.#primed(part, null);
            },
            (error: unknown): void => {
                // A refused start is a DOMException, named by the reason
                this.#primed(part, error instanceof Error ? error.name : 'Error');
            }
        );
    }

    /** A start of the part ended; a played part is seeked back to the wanted time it may have run past */
    #primed(part: number, errorName: string | null): void {
        this.#priming[part] = false;
        if (this.#stopped) {
            return;
        }
        const video: HTMLVideoElement = this.#videos[part];
        if (!video.paused) {
            video.pause();
        }
        // Whatever the start did, the frame the video stands on is unknown: the wanted one (the first
        // frame if none yet) is sought again. The seek also settles WebKit, whose clock was seen running
        // on to the end of a part paused in the very `playing` that started it. A video that knows no
        // metadata yet is not sought: it would never answer with `seeked`, and the part would stay held
        this.#landed[part] = -1;
        if (video.readyState >= video.HAVE_METADATA) {
            this.#seek(part, Math.max(0, this.#wanted[part]));
        }
        if (heroPrimeOutcome(errorName) !== EHeroPrime.Gesture) {
            this.#primeSettled[part] = true;
        }
    }

    /**
     * Safari on a Mac was seen playing a part by itself after its start and pause, until it stuck
     * mid-way: the clock ran on although the scroll stood. On every frame a part that plays, or whose
     * time left the frame it was sought to, is paused and sought back to the wanted time
     */
    #holdStill(): void {
        this.#videos.forEach((video: HTMLVideoElement, part: number): void => {
            if (!this.#sourced[part] || this.#priming[part] || this.#seeking[part]) {
                return;
            }
            const playing: boolean = !video.paused;
            const drifted: boolean = this.#landed[part] >= 0 && Math.abs(video.currentTime - this.#landed[part]) > HALF_FRAME;
            if (!playing && !drifted) {
                return;
            }
            if (playing) {
                video.pause();
            }
            this.#landed[part] = -1;
            if (video.readyState >= video.HAVE_METADATA) {
                this.#seek(part, Math.max(0, this.#wanted[part]));
            }
        });
    }

    #seek(part: number, time: number): void {
        this.#wanted[part] = time;
        if (this.#seeking[part] || Math.abs(this.#landed[part] - time) <= HALF_FRAME) {
            return;
        }
        this.#seeking[part] = true;
        this.#videos[part].currentTime = time;
    }

    #seeked(part: number): void {
        this.#seeking[part] = false;
        this.#landed[part] = this.#videos[part].currentTime;
        if (Math.abs(this.#wanted[part] - this.#landed[part]) > HALF_FRAME) {
            this.#seek(part, this.#wanted[part]);
            return;
        }
        if (part === this.#targetPart) {
            this.#show(part);
        }
    }

    #show(part: number): void {
        if (this.#shownPart !== part && this.#landed[part] >= 0) {
            this.#shownPart = part;
            this.#events.shown(part);
        }
    }
}
